import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCurrentAgentRunId, publishAgentRunEvent } from '../../run/agent-run-store';
import { captureNoteGenerationReviewed } from '../../note-generation-session-store';
import {
  applyPendingReview,
  getPlainReviewSuggestions,
  hasReviewCounters,
  markReviewHydrated,
  toPlainPendingReview,
  toPlainReviewSuggestion,
  type PendingReview,
  type ReviewSuggestion,
} from '../persisted-review';
import {
  bumpEditorHydrationGeneration,
  hasTerminalResolutionStarted,
  setTerminalResolutionStarted,
} from '@/hooks/llm/editor-suggestion-config';
import type { PersistedDocumentWriter, ReviewAdapter } from '../review-session-types';
import type { PersistedDocument } from '../../document-utils';
import { getEditorHTML, isEditorAlive } from '@/utils/editor-utils';

export function useReviewActions({
  requestId,
  pendingReview,
  persistDocumentPatch,
  editor,
  document,
}: {
  requestId: string;
  pendingReview: PendingReview | null;
  persistDocumentPatch: PersistedDocumentWriter;
  editor: Editor | null;
  document?: PersistedDocument;
}): ReviewAdapter | null {
  const finalizeResolvedReview = useCallback(
    async (contentChanged: boolean, onResolved?: (contentChanged: boolean) => void) => {
      onResolved?.(contentChanged);
      const runId = getCurrentAgentRunId(requestId);
      if (runId) {
        publishAgentRunEvent(requestId, runId, { type: 'review_resolved' });
      }
    },
    [requestId]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const suggestions = useMemo(() => pendingReview?.suggestions ?? [], [pendingReview]);

  useEffect(() => {
    setCurrentIndex((current) => Math.min(current, Math.max(suggestions.length - 1, 0)));
  }, [suggestions.length]);

  // The persisted acceptedCount base is handed to the suggestion accumulator (useSuggestionHandlers)
  // as its seed, so the `batchAcceptedCount` we receive is already the exact number to persist — no
  // second base is added here. A remount re-seeds the accumulator from the persisted doc, which
  // already includes prior batch counts.
  const acceptedCountBase = pendingReview && hasReviewCounters(pendingReview) ? pendingReview.acceptedCount : 0;

  const setSuggestions = useCallback(
    (suggestions: ReviewSuggestion[], options?: { batchAcceptedCount?: number }) => {
      const nextSuggestions = suggestions.map(toPlainReviewSuggestion);

      if (!pendingReview || !editor || !nextSuggestions.length) {
        return;
      }

      const nextPendingReview = toPlainPendingReview({
        ...pendingReview,
        reviewDocJson: editor.state.doc.toJSON(),
        suggestions: nextSuggestions,
        ...(hasReviewCounters(pendingReview) ? { acceptedCount: options?.batchAcceptedCount ?? 0 } : {}),
      });

      // The editor already reflects this payload, so its own emission must not re-hydrate it.
      markReviewHydrated(editor, nextPendingReview);

      void persistDocumentPatch({
        pendingReview: nextPendingReview,
      });
    },
    [editor, pendingReview, persistDocumentPatch]
  );

  const ensureLiveSuggestions = useCallback(
    (toolkit: ReturnType<typeof getAiToolkit>) => {
      // While terminal resolution is in flight the toolkit is legitimately empty but the persisted
      // pendingReview prop has not emitted null yet; healing here would re-apply a resolved review.
      if (!isEditorAlive(editor) || hasTerminalResolutionStarted(editor) || !pendingReview?.suggestions.length) return;

      const liveSuggestionCount = toolkit.getSuggestions().length;
      if (liveSuggestionCount > 0) return;

      // A legitimate final resolution still holds its last live suggestion at click time, so zero
      // live suggestions with persisted ones means the toolkit lost the review; resolving now
      // would persist the pre-review editor content as final.
      console.error(
        `Pending review desync for request ${requestId}: ${liveSuggestionCount} live toolkit suggestions vs ` +
          `${pendingReview.suggestions.length} persisted; re-applying the persisted review before resolving`
      );
      applyPendingReview(editor, pendingReview);
    },
    [editor, pendingReview, requestId]
  );

  const handleAllResolved = useCallback(
    async (contentChanged: boolean, batchAcceptedCount: number, onResolved?: (contentChanged: boolean) => void) => {
      if (!isEditorAlive(editor)) {
        return;
      }
      const resolvedReview = pendingReview;
      setTerminalResolutionStarted(editor, true);
      bumpEditorHydrationGeneration(editor);
      try {
        await persistDocumentPatch({
          content: getEditorHTML(editor),
          pendingReview: null,
        });
      } catch (error) {
        setTerminalResolutionStarted(editor, false);
        throw error;
      }

      captureNoteGenerationReviewed({ document, pendingReview: resolvedReview, acceptedCount: batchAcceptedCount });

      await finalizeResolvedReview(contentChanged, onResolved);
    },
    [document, editor, finalizeResolvedReview, pendingReview, persistDocumentPatch]
  );

  return useMemo(() => {
    if (!pendingReview || !editor || suggestions.length === 0) {
      return null;
    }

    return {
      currentIndex,
      suggestions,
      acceptedCountBase,
      setCurrentIndex,
      setSuggestions,
      getCurrentSuggestions: (toolkit: ReturnType<typeof getAiToolkit>) => getPlainReviewSuggestions(toolkit),
      ensureLiveSuggestions,
      handleAllResolved,
    };
  }, [
    acceptedCountBase,
    currentIndex,
    editor,
    ensureLiveSuggestions,
    handleAllResolved,
    pendingReview,
    setSuggestions,
    suggestions,
  ]);
}
